import React, { useState, useCallback, useEffect, useContext } from 'react';
import Dialog from '../../../../BaseComponents/Dialog/EDDialog';
import KolumnValjare, { IKolumnValjarProps } from '../../KolumnValjare';
import { Theme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import EDTabs from '../../../../BaseComponents/Tabs/EDTabs';
import EDTab from '../../../../BaseComponents/Tabs/Tab/EDTab';
import SorteringsValjare from '../../../SorteringsValjare/SorteringsValjare';
import { ISortInfo } from '../../../../BaseComponents/Table/Components/SortableTableUtils';
import { ICollectionDataType } from '../../../../Features/Delsystem/_Common/Components/BusinessCollectionComponents/Queries/BusinessCollectionQuery';
import { IColumnDef } from '../../../../Types/ColumnDefType';
import { useIsExtraSmallScreen } from '../../../../Hooks/useFullscreenView';
import { ModelContext } from '../../../../Model/ModelContext/ModelContext';
import { VisningsTypContext } from '../../../../Features/Delsystem/_Common/Components/BusinessCollectionComponents/VisningsTypContext/VisningsTypContext';
import { rutiner } from '../../../../Features/Delsystem/_Common/Const/Rutiner';
import { rutinKeys } from '../../../../Features/Delsystem/_Common/Const/RutinInfo';
import { ShowDetailsRight } from '../../../../Features/Delsystem/_Common/Const/Delsystem';
import { useIsStyrvarde } from '../../../../Hooks/useIsStyrvarde';
import { styrvarden } from '../../../../Features/Delsystem/_Common/Const/Styrvarden';
import KolumnSettingSelect, {
  favouriteRoutineLayoutSettingValueName,
} from './Components/KolumnSettingSelect/KolumnSettingSelect';
import KolumnValjareFunctionMenu from './Components/KolumnValjareFunctionMenu/KolumnValjareFunctionMenu';
import saveFavouriteRoutineLayoutMutation from './Mutations/SaveFavouriteRoutineLayoutMutation.gql';
import { useMutation, useQuery } from '@apollo/client';
import { ActiveDatabaseContext } from '../../../ActiveDatabaseContext/ActiveDatabaseContext';
import { RutinSettingsContext } from '../../../../Features/Delsystem/_Common/Components/RutinAvailabilityContext/RutinAvailabilityContext';
import {
  SaveFavouriteRoutineLayoutMutation,
  SaveFavouriteRoutineLayoutMutationVariables,
} from './Mutations/_QueryTypes/SaveFavouriteRoutineLayoutMutation';
import {
  ActiveRoutineContext,
  defaultRoutineLayoutId,
  getActiveRoutineQueryVariables,
  getDefaultRoutineLayout,
} from '../../../../Features/Routing/ActiveRoutine/ActiveRoutineContext';

import { padRoutineId } from '../../../../Utils/dbSettingsUtils';
import { ErrorDialogContext } from '../../../../BaseComponents/ErrorDialogManager/ErrorDialogManager';
import useShowDetailsRightDisabledOnCurrentScreenSize from '../../../../Features/Delsystem/_StandardRutin/Hooks/useShowDetailsRightDisabledOnCurrentScreenSize';
import {
  ActiveRoutineQuery,
  ActiveRoutineQueryVariables,
} from '../../../../Features/Routing/ActiveRoutine/_QueryTypes/ActiveRoutineQuery';
import activeRoutineQuery from '../../../../Features/Routing/ActiveRoutine/ActiveRoutineQuery.gql';
import Logger from '../../../../Utils/Logger';
import { assocPath } from 'ramda';
import { isDevelopment } from '../../../../Utils/isDevelopment';
import { useWidth } from '../../../BreakpointContext/BreakpointContextUtils';
import { getColumnWithCaption } from './KolumnValjareDialogUtils';
import { KodplanContext } from '../../../KodplanManager/Context/KodplanContext';

interface IProps extends IKolumnValjarProps {
  open: boolean;
  onClose: () => void;
  sortedData: ISortInfo<ICollectionDataType>;
  updateSortOrder: (info: ISortInfo<ICollectionDataType>) => void;
  tabValue: number;
  setTabValue: (value: number) => void;
  inCard?: boolean;
  inAssociation?: boolean;
}

const useStyles = makeStyles()(
  (theme: Theme) =>
    ({
      dialogPaper: {
        maxWidth: 'inherit',
      },
      dialogHeader: {
        '&&': { padding: 0 },
      },
      topArea: {
        padding: '24px 24px 20px',
      },
      bottomArea: {
        boxShadow: theme.shadows[3],
      },
      flexContainer: {
        flex: 1,
      },
      smallTab: {
        flex: 1,
        padding: 0,
      },
    } as const)
);

const KolumnValjareDialog: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const { setValdaKolumner, updateSortOrder, onClose } = props;
  const { classes, cx } = useStyles();
  const modelContext = useContext(ModelContext);
  const visningsTypContext = useContext(VisningsTypContext);
  const activeDatabaseContext = useContext(ActiveDatabaseContext);
  const { activeRoutine } = useContext(RutinSettingsContext);
  const activeRoutineContext = useContext(ActiveRoutineContext);
  const sparadeLayouter = activeRoutineContext?.sparadeLayouter;
  const setSelectedLayout = activeRoutineContext?.setSelectedLayout;
  const errorDialogContext = useContext(ErrorDialogContext);
  const kodplanContext = useContext(KodplanContext);
  const activeRoutineId = activeRoutine?.routine?.rutinID;
  const [lokalVisningsTyp, setLokalVisningsTyp] = useState(
    visningsTypContext.visningsTyp
  );
  const { setVisningsTyp, setShowDetailsRight } = visningsTypContext;
  useEffect(
    () => setLokalVisningsTyp(visningsTypContext.visningsTyp),
    [visningsTypContext.visningsTyp]
  );
  const [lokalShowDetailsRight, setLokalShowDetailsRight] = useState(
    Boolean(visningsTypContext.showDetailsRight)
  );
  const [valdaKolumner, setLokalaValdaKolumner] = useState<
    readonly IColumnDef[]
  >(() =>
    props.valdaKolumner.map(column =>
      getColumnWithCaption(props.collectionName, modelContext, column)
    )
  );
  const [showDevOptions, setShowDevOptions] = useState(false);
  useEffect(() => {
    setLokalaValdaKolumner(
      props.valdaKolumner.map(column =>
        getColumnWithCaption(props.collectionName, modelContext, column)
      )
    );
  }, [modelContext, props.collectionName, props.valdaKolumner]);
  const [sortOrder, setSortOrder] = useState(props.sortedData.properties);
  const [selectedKolumnSettingId, setSelectedKolumnSettingId] = useState<
    string | null
  >(null);

  const onExited = useCallback(() => {
    setLokalVisningsTyp(visningsTypContext.visningsTyp);
    setLokalShowDetailsRight(Boolean(visningsTypContext.showDetailsRight));
    setSelectedKolumnSettingId(null);
  }, [visningsTypContext.showDetailsRight, visningsTypContext.visningsTyp]);
  const updateValdaKolumner = useCallback(
    (kolumner: readonly IColumnDef[]) => {
      setSortOrder(
        sortOrder &&
          sortOrder.filter(x =>
            kolumner.some(kolumn => kolumn.attributeDef === x.orderBy)
          )
      );
      setLokalaValdaKolumner(kolumner);
    },
    [setSortOrder, sortOrder, setLokalaValdaKolumner]
  );

  const onSave = useCallback(() => {
    setVisningsTyp(lokalVisningsTyp);
    if (setShowDetailsRight) {
      setShowDetailsRight(lokalShowDetailsRight);
    }
    setValdaKolumner(valdaKolumner);
    updateSortOrder({
      rows: props.sortedData.rows,
      properties: sortOrder,
    });
    onClose();

    const selectedKolumnSetting = sparadeLayouter?.find(
      sparadLayout => sparadLayout.id === selectedKolumnSettingId
    );
    if (selectedKolumnSetting) {
      setSelectedLayout?.(selectedKolumnSetting);
    } else {
      setSelectedLayout?.(null);
    }
  }, [
    setVisningsTyp,
    setShowDetailsRight,
    lokalVisningsTyp,
    setValdaKolumner,
    updateSortOrder,
    props.sortedData.rows,
    onClose,
    valdaKolumner,
    sortOrder,
    sparadeLayouter,
    setSelectedLayout,
    lokalShowDetailsRight,
    selectedKolumnSettingId,
  ]);

  const skip = !activeDatabaseContext.activeDatabase || activeRoutineId == null;

  const { data } = useQuery<ActiveRoutineQuery, ActiveRoutineQueryVariables>(
    activeRoutineQuery,
    {
      variables: getActiveRoutineQueryVariables(
        activeDatabaseContext.activeDatabase,
        activeRoutineId,
        props.collectionName,
        kodplanContext.activeKodplan?.id
      ),
      skip,
      nextFetchPolicy: 'cache-only',
    }
  );

  useEffect(() => {
    setSortOrder(props.sortedData.properties);
  }, [props.sortedData.properties]);

  const [saveFavouriteRoutineLayoutFn] = useMutation<
    SaveFavouriteRoutineLayoutMutation,
    SaveFavouriteRoutineLayoutMutationVariables
  >(saveFavouriteRoutineLayoutMutation, {
    onError: error => {
      errorDialogContext.error('Kunde inte ändra favoritinställning', error);
    },
  });

  const saveFavouriteRoutineLayout = (id: string | null) => {
    if (activeDatabaseContext.activeDatabase && activeRoutineId) {
      saveFavouriteRoutineLayoutFn({
        variables: {
          dbName: activeDatabaseContext.activeDatabase,
          sectionName: `Forms/${padRoutineId(activeRoutineId)}`,
          newFavouriteRoutineLayoutId: id,
        },
        update: (cache, result) => {
          if (result.errors) {
            return;
          }
          const activeRoutineQueryVariables = getActiveRoutineQueryVariables(
            activeDatabaseContext.activeDatabase,
            activeRoutineId,
            props.collectionName,
            kodplanContext.activeKodplan?.id
          );
          const oldData = cache.readQuery<
            ActiveRoutineQuery,
            ActiveRoutineQueryVariables
          >({
            query: activeRoutineQuery,
            variables: activeRoutineQueryVariables,
          });

          const newFavouriteRoutineLayoutValue =
            result.data?.savedbsettings?.[0]?.values?.find(
              value => value.name === favouriteRoutineLayoutSettingValueName
            );

          if (newFavouriteRoutineLayoutValue) {
            if (oldData && oldData.me?.[0]) {
              const routineSettingsSectionName = padRoutineId(activeRoutineId);
              let routineSettingsSectionIndex =
                oldData.me?.[0]?.favoritRoutineLayout?.findIndex(
                  settingSections =>
                    settingSections.name === routineSettingsSectionName
                );
              const favouriteRoutineLayoutValueIndex =
                oldData.me?.[0]?.favoritRoutineLayout?.[
                  routineSettingsSectionIndex ?? -1
                ]?.values?.findIndex(
                  value => value.name === favouriteRoutineLayoutSettingValueName
                );

              let newData = oldData;

              if (
                routineSettingsSectionIndex != null &&
                routineSettingsSectionIndex >= 0 &&
                favouriteRoutineLayoutValueIndex != null &&
                favouriteRoutineLayoutValueIndex >= 0
              ) {
                // routineSettingsSection and favouriteRoutineLayoutValue already exists in cache, dont have to do anything
                return;
              } else {
                if (
                  !routineSettingsSectionIndex ||
                  routineSettingsSectionIndex === -1
                ) {
                  newData = assocPath(
                    ['me', 0, 'favoritRoutineLayout'],
                    [
                      {
                        __typename: 'TEDSettingsSection',
                        id: 'favoritRoutineLayoutSettingsSectionTempId',
                        name: routineSettingsSectionName,
                      },
                    ],
                    newData
                  );
                  routineSettingsSectionIndex = 0;
                }

                if (
                  !favouriteRoutineLayoutValueIndex ||
                  favouriteRoutineLayoutValueIndex === -1
                ) {
                  newData = assocPath(
                    [
                      'me',
                      0,
                      'favoritRoutineLayout',
                      routineSettingsSectionIndex,
                      'values',
                    ],
                    [newFavouriteRoutineLayoutValue],
                    newData
                  );
                }

                cache.writeQuery<
                  ActiveRoutineQuery,
                  ActiveRoutineQueryVariables
                >({
                  query: activeRoutineQuery,
                  data: newData,
                  variables: activeRoutineQueryVariables,
                });
              }
            } else {
              Logger.error(
                new Error(
                  'Error while updating cache after saveFavouriteRoutineLayoutFn, oldData not found'
                )
              );
            }
          } else {
            Logger.error(
              new Error(
                'Error while updating cache after saveFavouriteRoutineLayoutFn, newFavouriteRoutineLayoutValue not found'
              )
            );
          }
        },
        optimisticResponse: {
          savedbsettings: [
            {
              id:
                data?.me?.[0]?.favoritRoutineLayout?.find(
                  settingSections =>
                    settingSections.name === padRoutineId(activeRoutineId)
                )?.id ?? 'tempNA_FavouriteRoutineLayoutId',
              name: padRoutineId(activeRoutineId),
              values: [
                {
                  id: 'tempNA_FavouriteRoutineLayoutId',
                  name: favouriteRoutineLayoutSettingValueName,
                  value: {
                    asString: id,
                    __typename: 'TEDMixedString',
                  },
                  __typename: 'TEDSettingsIdent',
                },
              ],
              __typename: 'TEDSettingsSection',
            },
          ],
        },
      });
    }
  };

  const showDetailsRightDisabledOnCurrentScreenSize =
    useShowDetailsRightDisabledOnCurrentScreenSize();
  const screenWidth = useWidth();

  const setSelectedKolumnSetting = (id: string | null) => {
    setSelectedKolumnSettingId(id);

    if (id) {
      if (id === defaultRoutineLayoutId) {
        const defaultRoutineLayout = getDefaultRoutineLayout(
          props.collectionName,
          modelContext,
          rutinKeys[visningsTypContext.rutinId],
          showDetailsRightDisabledOnCurrentScreenSize,
          screenWidth
        );

        setLokalVisningsTyp(defaultRoutineLayout.visningstyp);
        updateValdaKolumner(defaultRoutineLayout.columns);
        setLokalShowDetailsRight(defaultRoutineLayout.showDetailsRight);
        setSortOrder(defaultRoutineLayout.sortering);
      } else {
        const selectedKolumnSetting =
          activeRoutineContext?.sparadeLayouter?.find(
            sparadLayout => sparadLayout.id === id
          );
        if (selectedKolumnSetting) {
          if (selectedKolumnSetting.visningstyp !== undefined)
            setLokalVisningsTyp(selectedKolumnSetting.visningstyp);
          if (selectedKolumnSetting.columns !== undefined)
            updateValdaKolumner(
              selectedKolumnSetting.columns.map(column =>
                getColumnWithCaption(props.collectionName, modelContext, column)
              )
            );
          if (selectedKolumnSetting.showDetailsRight !== undefined)
            setLokalShowDetailsRight(selectedKolumnSetting.showDetailsRight);
          if (selectedKolumnSetting.sortering !== undefined)
            setSortOrder(selectedKolumnSetting.sortering);
        }
      }
    }
  };

  useEffect(() => {
    setSelectedKolumnSettingId(
      activeRoutineContext?.selectedLayout
        ? activeRoutineContext.selectedLayout.id
        : defaultRoutineLayoutId
    );
  }, [activeRoutineContext?.selectedLayout, props.open]);

  const isSmall = useIsExtraSmallScreen();
  const isStyrvarde = useIsStyrvarde();

  const showDetailsRightDisabled = isStyrvarde
    ? styrvarden()[rutinKeys[visningsTypContext.rutinId]]
        ?.defaultShowDetailsRight === ShowDetailsRight.Disabled
    : rutiner[rutinKeys[visningsTypContext.rutinId]]
        ?.defaultShowDetailsRight === ShowDetailsRight.Disabled;

  const visningsTypShowDetailsRight = isStyrvarde
    ? styrvarden()[rutinKeys[visningsTypContext.rutinId]]
        ?.defaultVisningsTypShowDetailsRight
    : rutiner[rutinKeys[visningsTypContext.rutinId]]
        ?.defaultVisningsTypShowDetailsRight;

  const defaultVisningsTyp = isStyrvarde
    ? styrvarden()[rutinKeys[visningsTypContext.rutinId]]?.defaultVisningsTyp
    : rutiner[rutinKeys[visningsTypContext.rutinId]]?.defaultVisningsTyp;

  return (
    <Dialog
      data-test={`dialog:Kolumnväljare`}
      open={props.open}
      title="Kolumner och sortering"
      onClose={props.onClose}
      classes={{
        paper: classes.dialogPaper,
        dialogHeader: classes.dialogHeader,
        topArea: classes.topArea,
        bottomArea: classes.bottomArea,
      }}
      footerActions={[
        { onClick: props.onClose, label: 'Avbryt' },
        { onClick: onSave, label: 'Använd' },
      ]}
      subtitle={
        !props.inCard &&
        !props.inAssociation && (
          <KolumnSettingSelect
            selectedKolumnSetting={selectedKolumnSettingId}
            setSelectedKolumnSetting={setSelectedKolumnSetting}
            favouriteKolumnSetting={activeRoutineContext?.favoritLayoutId}
            setFavouriteKolumnSetting={saveFavouriteRoutineLayout}
            collectionName={props.collectionName}
          />
        )
      }
      topToolbarArea={
        (isDevelopment || (!props.inCard && !props.inAssociation)) && (
          <KolumnValjareFunctionMenu
            selectedKolumnSetting={selectedKolumnSettingId}
            setSelectedKolumnSetting={setSelectedKolumnSetting}
            favouriteKolumnSetting={activeRoutineContext?.favoritLayoutId}
            setFavouriteKolumnSetting={saveFavouriteRoutineLayout}
            valdaKolumner={valdaKolumner}
            showDetailsRight={lokalShowDetailsRight}
            sortOrder={sortOrder}
            visningstyp={lokalVisningsTyp}
            showDevOptions={showDevOptions}
            setShowDevOptions={setShowDevOptions}
            collectionName={props.collectionName}
          />
        )
      }
      headerBottomArea={
        <EDTabs
          value={props.tabValue}
          onChange={props.setTabValue}
          indicatorColor="white"
          variant="fullWidth"
          classes={{
            root: cx({ [classes.flexContainer]: isSmall }),
            flexContainer: cx({ [classes.flexContainer]: isSmall }),
          }}
          noBoxShadow
        >
          <EDTab
            label="Kolumner"
            className={cx({ [classes.smallTab]: isSmall })}
          />
          <EDTab
            label="Sortering"
            className={cx({ [classes.smallTab]: isSmall })}
          />
        </EDTabs>
      }
      fullHeight={!isSmall}
      onExited={onExited}
    >
      {props.tabValue === 0 && (
        <KolumnValjare
          collectionName={props.collectionName}
          visningstyp={lokalVisningsTyp}
          setVisningstyp={setLokalVisningsTyp}
          valdaKolumner={valdaKolumner}
          setValdaKolumner={updateValdaKolumner}
          inCard={props.inCard}
          showDetailsRight={lokalShowDetailsRight}
          setShowDetailsRight={setLokalShowDetailsRight}
          showDetailsRightDisabled={showDetailsRightDisabled}
          visningsTypShowDetailsRight={visningsTypShowDetailsRight}
          defaultVisningsTyp={defaultVisningsTyp}
          selectedKolumnSettingId={selectedKolumnSettingId}
          showDevOptions={showDevOptions}
        />
      )}
      {props.tabValue === 1 && (
        <SorteringsValjare
          kolumner={valdaKolumner || []}
          sortProperties={sortOrder || []}
          updateSortProperties={setSortOrder}
        />
      )}
    </Dialog>
  );
};

export default KolumnValjareDialog;
